import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApiService } from '../services/authApi';
import { AUTH_ERROR_MESSAGES, AUTH_ROUTES } from '../constants';
import AuthHero from '../components/AuthHero';

const ForgotPassword: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [emailSent, setEmailSent] = useState(false);

    // Handle error/success messages from other pages (like expired token redirects)
    useEffect(() => {
        if (location.state?.message) {
            setMessage(location.state.message);
            setEmailSent(true);
        }
        if (location.state?.error) {
            setError(location.state.error);
        }
        
        // Clear location state to prevent showing messages on refresh
        if (location.state) {
            navigate(location.pathname, { replace: true, state: undefined });
        }
    }, [location.state, navigate, location.pathname]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Clear previous messages
        setError('');
        setMessage('');
        setIsLoading(true);

        try {
            // Trim and validate email
            const trimmedEmail = email.trim().toLowerCase();
            
            if (!trimmedEmail) {
                throw new Error(AUTH_ERROR_MESSAGES.EMAIL_REQUIRED);
            }

            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
                throw new Error(AUTH_ERROR_MESSAGES.INVALID_EMAIL);
            }


            // Send reset request using the updated API
            const response = await authApiService.forgotPassword(trimmedEmail);
            
            
            navigate(AUTH_ROUTES.CHECK_INBOX, {
            state: { 
                email: trimmedEmail,
                message: response.message 
            }
        });
            
        } catch (err: any) {
            console.error('❌ DEBUG: Forgot password error:', err);
            
            // Handle specific error types
            let errorMessage = err.message || AUTH_ERROR_MESSAGES.NETWORK_ERROR;
            
            // Map API errors to user-friendly messages
            if (errorMessage === AUTH_ERROR_MESSAGES.EMAIL_REQUIRED) {
                setError('Please enter your email address');
            } else if (errorMessage === AUTH_ERROR_MESSAGES.INVALID_EMAIL) {
                setError('Please enter a valid email address');
            } else if (errorMessage.includes('timeout') || errorMessage.includes('network')) {
                setError('Connection error. Please check your internet and try again.');
            } else {
                // For security, show generic message for any other errors
                setError('Something went wrong. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToLogin = () => {
        navigate(AUTH_ROUTES.LOGIN);
    };

    const handleTryAnotherEmail = () => {
        setEmailSent(false);
        setMessage('');
        setError('');
        setEmail('');
    };

    // Success state - Email sent confirmation
    if (emailSent && message) {
        return (
            <div className="min-h-screen bg-[#F8F9FB] flex">
                {/* Left Side - Success Message */}
                <div className="flex-1 flex items-center justify-center p-8">
                    <div className="w-full max-w-md">
                        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                            {/* Logo */}
                            <div className="mb-8">
                                <img
                                    className="mx-auto h-12 w-auto"
                                    src="/Group-1321314269.jpg"
                                    alt="Hush"
                                />
                            </div>

                            {/* Success Icon */}
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
                                <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 001.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </div>

                            {/* Success Message */}
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">
                                Check your inbox
                            </h2>

                            <p className="text-gray-600 mb-6">
                                We've sent a password reset link to{' '}
                                <span className="font-semibold text-gray-800">{email}</span>
                            </p>

                            {/* Success Details */}
                            <div className="bg-green-50 rounded-lg p-4 mb-6 text-left">
                                <p className="text-sm text-green-700">{message}</p>
                            </div>

                            {/* Instructions */}
                            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                                <p className="text-sm font-medium text-gray-900 mb-2">Didn't receive the email?</p>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Check your spam or junk folder</li>
                                    <li>• Make sure you entered the correct email address</li>
                                    <li>• The link will expire in 24 hours</li>
                                    <li>• Contact support if you continue having issues</li>
                                </ul>
                            </div>

                            {/* Action Buttons */}
                            <div className="space-y-3">
                                <button
                                    onClick={handleBackToLogin}
                                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                                >
                                    Back to login
                                </button>
                                
                                <button
                                    onClick={handleTryAnotherEmail}
                                    className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                                >
                                    Try another email
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side - Hero */}
                <div className="flex-1">
                    <AuthHero />
                </div>
            </div>
        );
    }

    // Form state - Email input
    return (
        <div
            className="min-h-screen flex items-start justify-center relative overflow-hidden pt-16"
            style={{
                background: '#015FA6',
                fontFamily: 'Manrope, sans-serif'
            }}
        >
            {/* Background decorative ellipse */}
            <div
                style={{
                    position: 'absolute',
                    width: '1078.81px',
                    height: '1064.85px',
                    left: '-514px',
                    bottom: '-209.85px',
                    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8) 0%, rgba(116, 153, 191, 0.465385) 41.83%, rgba(237, 242, 247, 0) 100%)',
                    transform: 'rotate(-153.16deg)',
                    borderRadius: '50%',
                    pointerEvents: 'none'
                }}
            />

            {/* Main content container */}
            <div className="w-full max-w-[480px] px-4 py-6 relative z-10">
                {/* Logo */}
                <div className="text-center mb-6">
                    <img
                        src="/branding/logo_blue.svg"
                        alt="Hush"
                        className="mx-auto cursor-pointer"
                        style={{
                            width: 'auto',
                            height: '50px',
                            objectFit: 'contain'
                        }}
                        onClick={() => navigate('/')}
                        onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                                parent.innerHTML = '<div style="font-size: 32px; font-weight: 700; color: white; font-family: Manrope;">Hush</div>';
                            }
                        }}
                    />
                </div>

                {/* Header */}
                <div className="text-center mb-8">
                    <h1
                        style={{
                            fontFamily: 'Manrope',
                            fontWeight: 700,
                            fontSize: '32px',
                            lineHeight: '100%',
                            color: '#FFFFFF',
                            marginBottom: '12px'
                        }}
                        className="text-2xl sm:text-3xl"
                    >
                        Forgot your password?
                    </h1>
                    <p
                        style={{
                            fontFamily: 'Manrope',
                            fontWeight: 300,
                            fontSize: '14px',
                            lineHeight: '150%',
                            textAlign: 'center',
                            color: '#FFFFFF'
                        }}
                        className="text-sm"
                    >
                        Enter the email address linked to your account,<br />
                        and we'll send you a link to reset your password.
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                    {/* Error Message */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-700">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Email Field */}
                    <div>
                        <label
                            htmlFor="email"
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '18px',
                                letterSpacing: '-0.154px',
                                color: '#FFFFFF',
                                display: 'block',
                                marginBottom: '6px'
                            }}
                        >
                            Email
                        </label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            required
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (error) setError('');
                            }}
                            placeholder="example@gmail.com"
                            disabled={isLoading}
                            style={{
                                width: '100%',
                                height: '45px',
                                padding: '0 14px',
                                fontFamily: 'Manrope',
                                fontSize: '14px',
                                lineHeight: '18px',
                                letterSpacing: '-0.154px',
                                color: 'rgba(1, 95, 166, 0.5)',
                                background: '#F7FAFC',
                                border: error ? '1px solid #EF4444' : '1px solid #CBD5E0',
                                boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                borderRadius: '10px',
                                outline: 'none'
                            }}
                            className="w-full"
                        />
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={isLoading || !email.trim()}
                        style={{
                            width: '100%',
                            height: '45px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontFamily: 'Manrope',
                            fontWeight: 600,
                            fontSize: '16px',
                            lineHeight: '22px',
                            color: '#015FA6',
                            background: '#F8F9FB',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: (isLoading || !email.trim()) ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            marginTop: '16px',
                            opacity: !email.trim() ? 0.6 : 1
                        }}
                        className="hover:shadow-lg hover:bg-white"
                    >
                        {isLoading ? 'Sending reset link...' : 'Send reset link'}
                    </button>

                    {/* Back to Login */}
                    <div className="text-center mt-4">
                        <button
                            type="button"
                            onClick={handleBackToLogin}
                            disabled={isLoading}
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 500,
                                fontSize: '14px',
                                color: '#FFFFFF',
                                background: 'none',
                                border: 'none',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                transition: 'opacity 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                margin: '0 auto'
                            }}
                            className="hover:opacity-80"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Back to sign in
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ForgotPassword;
